// ---------------------------------------------------------------------------
// r2UploadService.js
// Uploads images directly to Cloudflare R2 via S3-compatible API.
// Mirrors the iOS R2StorageService / R2Config implementation exactly.
// ---------------------------------------------------------------------------

const R2_ACCOUNT_ID   = '466e621450c0c071d16bdddaeac4e725';
const R2_ACCESS_KEY   = 'a99321c000ea7ee6a2d08ea8d16f2a33';
const R2_SECRET_KEY   = 'c839ec681bd84d6298124cee49c8b2e684cf94974c7b1af61d91debe52e91a5c';
const R2_BUCKET       = 'nutrius-images';
const R2_ENDPOINT     = `https://${R2_ACCOUNT_ID}.r2.cloudflarestorage.com`;
const R2_PUBLIC_URL   = 'https://pub-69113aa5c2284f5f9ad0ee7e052a57ea.r2.dev';

// ---------------------------------------------------------------------------
// Resize a File/Blob to a max dimension (longest side), returns a Blob
// ---------------------------------------------------------------------------
async function resizeImage(file, maxDimension) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      const { width, height } = img;
      const aspectRatio = width / height;

      let targetWidth, targetHeight;
      if (aspectRatio > 1) {
        targetWidth  = maxDimension;
        targetHeight = Math.round(maxDimension / aspectRatio);
      } else {
        targetWidth  = Math.round(maxDimension * aspectRatio);
        targetHeight = maxDimension;
      }

      const canvas = document.createElement('canvas');
      canvas.width  = targetWidth;
      canvas.height = targetHeight;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

      canvas.toBlob(
        blob => blob ? resolve(blob) : reject(new Error('Canvas toBlob failed')),
        'image/jpeg',
        0.8
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load image'));
    };

    img.src = objectUrl;
  });
}

// ---------------------------------------------------------------------------
// AWS Signature V4 — signs a single PUT request for R2
// ---------------------------------------------------------------------------
async function signRequest({ method, url, body, contentType, date }) {
  const endpoint   = new URL(url);
  const dateStr    = date.toISOString().replace(/[:-]|\.\d{3}/g, '').slice(0, 15) + 'Z'; // YYYYMMDDTHHmmssZ
  const dateSimple = dateStr.slice(0, 8); // YYYYMMDD
  const region     = 'auto';
  const service    = 's3';

  // Canonical request
  const payloadHash = await sha256Hex(body);
  const canonicalHeaders =
    `content-type:${contentType}\n` +
    `host:${endpoint.host}\n` +
    `x-amz-content-sha256:${payloadHash}\n` +
    `x-amz-date:${dateStr}\n`;
  const signedHeaders = 'content-type;host;x-amz-content-sha256;x-amz-date';
  const canonicalPath = endpoint.pathname;
  const canonicalRequest = [
    method,
    canonicalPath,
    '',                  // query string
    canonicalHeaders,
    signedHeaders,
    payloadHash,
  ].join('\n');

  // String to sign
  const credentialScope = `${dateSimple}/${region}/${service}/aws4_request`;
  const stringToSign = [
    'AWS4-HMAC-SHA256',
    dateStr,
    credentialScope,
    await sha256Hex(canonicalRequest),
  ].join('\n');

  // Signing key
  const signingKey = await deriveSigningKey(R2_SECRET_KEY, dateSimple, region, service);
  const signature  = await hmacHex(signingKey, stringToSign);

  const authHeader =
    `AWS4-HMAC-SHA256 Credential=${R2_ACCESS_KEY}/${credentialScope}, ` +
    `SignedHeaders=${signedHeaders}, ` +
    `Signature=${signature}`;

  return {
    Authorization:          authHeader,
    'Content-Type':         contentType,
    'x-amz-date':           dateStr,
    'x-amz-content-sha256': payloadHash,
  };
}

// ---------------------------------------------------------------------------
// Upload a single Blob to R2, returns the public URL
// ---------------------------------------------------------------------------
async function putToR2(blob, key) {
  const url  = `${R2_ENDPOINT}/${R2_BUCKET}/${key}`;
  const date = new Date();
  const contentType = 'image/jpeg';

  const arrayBuffer = await blob.arrayBuffer();
  const headers     = await signRequest({
    method: 'PUT',
    url,
    body: arrayBuffer,
    contentType,
    date,
  });

  const response = await fetch(url, {
    method: 'PUT',
    headers,
    body: blob,
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`R2 upload failed: ${response.status} ${response.statusText} — ${text}`);
  }

  return `${R2_PUBLIC_URL}/${key}`;
}

// ---------------------------------------------------------------------------
// Public API — matches the existing call signature used in CreatePostModal
//
//   uploadImageToR2(file, entityType, entityId, purpose)
//   → { urls: { thumb, medium, full } }
// ---------------------------------------------------------------------------
export async function uploadImageToR2(file, entityType, entityId, purpose) {
  // Validate minimum dimensions
  await validateImage(file);

  const uploadId = crypto.randomUUID ? crypto.randomUUID() : generateId();
  const basePath = `${entityType}/${entityId}/${purpose}/${uploadId}`;

  // Resize to three sizes in parallel (matches iOS: 150 / 600 / 1200)
  const [thumbBlob, mediumBlob, fullBlob] = await Promise.all([
    resizeImage(file, 150),
    resizeImage(file, 600),
    resizeImage(file, 1200),
  ]);

  // Upload all sizes in parallel
  const [thumb, medium, full] = await Promise.all([
    putToR2(thumbBlob,  `${basePath}_thumb.jpg`),
    putToR2(mediumBlob, `${basePath}_medium.jpg`),
    putToR2(fullBlob,   `${basePath}_full.jpg`),
  ]);

  return { urls: { thumb, medium, full } };
}

// ---------------------------------------------------------------------------
// Validation — mirrors iOS validateImage (200px min, 10MB max)
// ---------------------------------------------------------------------------
async function validateImage(file) {
  if (file.size > 10_000_000) {
    throw new Error('Image must be under 10MB');
  }

  await new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      if (img.width < 200 || img.height < 200) {
        reject(new Error('Image must be at least 200x200px'));
      } else {
        resolve();
      }
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Invalid image')); };
    img.src = url;
  });
}

// ---------------------------------------------------------------------------
// Crypto helpers (Web Crypto API)
// ---------------------------------------------------------------------------
async function sha256Hex(data) {
  const buf    = typeof data === 'string' ? new TextEncoder().encode(data) : data;
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return hex(digest);
}

async function hmacHex(keyBytes, message) {
  const key = await crypto.subtle.importKey(
    'raw', keyBytes, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']
  );
  const sig = await crypto.subtle.sign('HMAC', key, new TextEncoder().encode(message));
  return hex(sig);
}

async function hmacBytes(keyBytes, message) {
  const key = await crypto.subtle.importKey(
    'raw', keyBytes, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']
  );
  const sig = await crypto.subtle.sign('HMAC', key, new TextEncoder().encode(message));
  return new Uint8Array(sig);
}

async function deriveSigningKey(secretKey, date, region, service) {
  const kDate    = await hmacBytes(new TextEncoder().encode(`AWS4${secretKey}`), date);
  const kRegion  = await hmacBytes(kDate, region);
  const kService = await hmacBytes(kRegion, service);
  return await hmacBytes(kService, 'aws4_request');
}

function hex(buffer) {
  return Array.from(new Uint8Array(buffer))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

function generateId() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    return (c === 'x' ? r : ((r & 0x3) | 0x8)).toString(16);
  });
}
